
import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import CTASection from '../../components/feature/CTASection';

export default function AboutPage() {
  useEffect(() => {
    document.title = 'About SparkBoost Labs | AI Revenue Systems Sacramento | Our Story & Team';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Meet the team behind SparkBoost Labs. Sacramento-based AI revenue systems experts helping service companies build predictable growth. Learn our story and methodology.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#1B365D] to-[#2BB6A3] py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h1 className="text-5xl font-bold mb-6">
                Building Predictable Revenue Systems Since 2023
              </h1>
              <p className="text-xl mb-8 opacity-90">
                We're on a mission to eliminate the feast-or-famine cycle for service-based companies 
                through AI-powered revenue systems and proven growth methodologies.
              </p>
            </div>
          </div>
        </section>

        {/* Our Story */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold text-[#1B365D] mb-8 text-center">Our Story</h2>
              
              <div className="prose prose-xl mx-auto text-gray-600">
                <p className="mb-6 text-lg">
                  SparkBoost Labs was founded with a simple yet powerful vision: to help service-based companies 
                  break free from unpredictable revenue cycles and build systematic, scalable growth engines.
                </p>
                
                <p className="mb-6 text-lg">
                  After working with hundreds of consultants, agencies, and professional service firms, we noticed 
                  a common pattern. Most successful companies were stuck in the "founder bottleneck" - entirely 
                  dependent on the founder's personal network and sales efforts to generate revenue.
                </p>
                
                <p className="mb-6 text-lg">
                  This created a feast-or-famine cycle where business owners were always just one month away from 
                  their best month or their worst month. They had built successful practices but couldn't scale 
                  beyond their personal capacity.
                </p>
                
                <p className="mb-6 text-lg">
                  We developed our methodology by studying what separated the companies that scaled predictably 
                  from those that remained stuck. The answer wasn't just better marketing or sales - it was 
                  systematic revenue operations powered by intelligent automation.
                </p>
                
                <p className="text-lg">
                  Today, we've helped over 75 companies implement these systems, with an average revenue increase 
                  of 300% within the first year. Our clients don't just grow - they build sustainable, predictable 
                  revenue engines that scale beyond founder dependency.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Founder Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-[#1B365D] mb-16 text-center">Meet Our Founder</h2>
              
              <div className="flex flex-col lg:flex-row items-center gap-12">
                <div className="lg:w-1/3">
                  <img
                    src="https://static.readdy.ai/image/f3375ab72d7f65b358dc6cf884e758f7/99d0d376faf7295cfa00e5627cf9bc5f.jpeg"
                    alt="James Hansen - SparkBoost Labs CEO and founder"
                    className="w-full max-w-sm mx-auto rounded-lg shadow-lg object-cover object-top"
                  />
                </div>
                
                <div className="lg:w-2/3">
                  <h3 className="text-3xl font-bold text-[#1B365D] mb-4">James Hansen</h3>
                  <p className="text-xl text-[#2BB6A3] mb-6">CEO & Founder</p>
                  
                  <div className="text-gray-600 mb-8 space-y-4">
                    <p className="text-lg">
                      James founded SparkBoost Labs after experiencing firsthand the challenges of scaling a 
                      service-based business. As a former management consultant, he struggled with the same 
                      feast-or-famine cycles that plague most professional service firms.
                    </p>
                    
                    <p className="text-lg">
                      After successfully implementing AI-powered revenue systems in his own consulting practice 
                      and achieving predictable 7-figure growth, James realized he had discovered something 
                      transformational that needed to be shared with other service-based entrepreneurs.
                    </p>
                    
                    <p className="text-lg">
                      With over 12 years of experience in business operations, sales optimization, and AI 
                      automation, James has become a recognized expert in building predictable revenue systems 
                      for professional service companies.
                    </p>
                    
                    <p className="text-lg">
                      When he's not helping clients build their revenue engines, James enjoys hiking in the 
                      Sacramento area, mentoring young entrepreneurs, and staying on the cutting edge of 
                      AI and automation technologies.
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-[#1B365D] mb-2">Expertise:</h4>
                      <ul className="text-gray-600 space-y-1">
                        <li>• Revenue Operations</li>
                        <li>• AI Automation Systems</li>
                        <li>• Sales Process Optimization</li>
                        <li>• Growth Strategy</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#1B365D] mb-2">Credentials:</h4>
                      <ul className="text-gray-600 space-y-1">
                        <li>• University of Wisconsin-Green Bay</li>
                        <li>• Bachelor's degree, Business Administration and Management</li>
                        <li>• HubSpot Elite Partner</li>
                        <li>• AI Automation Specialist</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Methodology */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-[#1B365D] mb-16 text-center">Our Methodology</h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-20 h-20 bg-[#2BB6A3] rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-brain-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-[#1B365D] mb-4">Systems Thinking</h3>
                  <p className="text-gray-600">
                    We approach revenue growth as an interconnected system, not isolated tactics. 
                    Every component works together to create predictable, scalable results.
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="w-20 h-20 bg-[#2BB6A3] rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-bar-chart-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-[#1B365D] mb-4">Data-Driven Decisions</h3>
                  <p className="text-gray-600">
                    Every strategy is backed by data and continuously optimized based on performance 
                    metrics. We measure what matters and improve systematically.
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="w-20 h-20 bg-[#2BB6A3] rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-rocket-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-[#1B365D] mb-4">Implementation Focus</h3>
                  <p className="text-gray-600">
                    We don't just create strategies - we implement them. Our hands-on approach 
                    ensures systems are built, tested, and optimized for maximum results.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold text-[#1B365D] mb-16 text-center">Our Values</h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white rounded-lg p-8 shadow-lg">
                  <h3 className="text-2xl font-bold text-[#1B365D] mb-4">Predictable Results</h3>
                  <p className="text-gray-600">
                    We believe business growth should be predictable, not left to chance. Our systems 
                    create consistent, measurable outcomes that scale with your business.
                  </p>
                </div>
                
                <div className="bg-white rounded-lg p-8 shadow-lg">
                  <h3 className="text-2xl font-bold text-[#1B365D] mb-4">Sustainable Growth</h3>
                  <p className="text-gray-600">
                    We focus on building systems that create long-term, sustainable growth rather than 
                    short-term tactics that burn out quickly.
                  </p>
                </div>
                
                <div className="bg-white rounded-lg p-8 shadow-lg">
                  <h3 className="text-2xl font-bold text-[#1B365D] mb-4">Client Partnership</h3>
                  <p className="text-gray-600">
                    We see ourselves as partners in your success, not just service providers. Your 
                    growth is our success, and we're invested in your long-term results.
                  </p>
                </div>
                
                <div className="bg-white rounded-lg p-8 shadow-lg">
                  <h3 className="text-2xl font-bold text-[#1B365D] mb-4">Continuous Innovation</h3>
                  <p className="text-gray-600">
                    We stay at the forefront of AI and automation technologies to ensure our clients 
                    always have access to the most effective growth systems available.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <CTASection />
      </main>
      <Footer />
    </div>
  );
}
